import React, { Component } from 'react';
import { Button, Card } from 'antd';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { withTranslation } from 'react-i18next';

/**
 * ErrorBoundary Component
 *
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI.
 *
 * @example
 * ```tsx
 * <ErrorBoundary onError={(error) => logToService(error)}>
 *   <MyComponent />
 * </ErrorBoundary>
 * ```
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });

    // Log error to console in development
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Call optional error handler
    this.props.onError?.(error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div
          role="alert"
          aria-live="assertive"
          className="flex items-center justify-center min-h-[400px] p-6"
        >
          <Card className="max-w-[500px] w-full text-center shadow-lg">
            <div className="py-8">
              <div className="flex justify-center mb-6">
                <div className="bg-red-50 rounded-full p-4">
                  <AlertCircle
                    className="text-red-500"
                    size={48}
                    aria-hidden="true"
                  />
                </div>
              </div>

              <h2 className="text-2xl font-semibold mb-2">
                {this.props.t('Something went wrong')}
              </h2>

              <p className="text-gray-600 mb-6">
                {this.props.t('Error message')}
              </p>

              {process.env.NODE_ENV === 'development' && this.state.error && (
                <div className="bg-gray-100 rounded-lg p-4 mb-6 text-left max-h-[150px] overflow-auto">
                  <pre className="font-mono text-xs whitespace-pre-wrap break-words m-0">
                    {this.state.error.toString()}
                    {this.state.errorInfo?.componentStack}
                  </pre>
                </div>
              )}

              <div className="flex gap-4 justify-center">
                <Button
                  type="default"
                  icon={<RefreshCw size={16} />}
                  onClick={this.handleReset}
                  aria-label="Try again without reloading"
                >
                  {this.props.t('Try Again')}
                </Button>
                <Button
                  type="primary"
                  icon={<RefreshCw size={16} />}
                  onClick={this.handleReload}
                  aria-label="Reload the page"
                >
                  {this.props.t('Reload Page')}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default withTranslation()(ErrorBoundary);
